import React, { useContext, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CommonItem, Corruption, Injury, Madness, SpecialCard, Spell, UniqueItem } from '../api';
import { corruptionPresentation } from '../utils/corruptionPresentation';
import type { StackedItemWithKind } from './Card';
import { GameDialog } from './GameDialog';
import { useSpellCastingPreview } from './SpellCastingContext';
import { SpellCostBreakdown } from './SpellCostBreakdown';
import { SpellRelatedSpecialCard } from './SpellRelatedSpecialCard';
import { ItemPurchaseContext, useItemWeaponPreview, useMiskatonicTraumaRules } from './ItemPresentationContext';
import { ItemPriceBreakdown } from './ItemPriceBreakdown';
import './DigitalItemCard.css';
import { observeElementResize } from './observeElementResize';

/** Measure layout dimensions, never transformed bounds (inventory cards can be rotated/scaled). */
const FittedCardText: React.FC<{ children: React.ReactNode; className: string; contentKey: string }> = ({ children, className, contentKey }) => {
  const ref = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = ref.current!;
    const content = contentRef.current!;
    let disposed = false;
    const fit = () => {
      if (disposed || !element.clientWidth || !element.clientHeight) return;
      const maximum = parseFloat(getComputedStyle(element).getPropertyValue('--fit-font-size')) || 14;
      const style = getComputedStyle(element);
      const availableHeight = element.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
      const availableWidth = element.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const fits = (size: number) => {
        element.style.fontSize = `${size}px`;
        // A centered overflowing child can extend above its parent without increasing
        // the parent's scrollHeight. Always measure the child's natural content size.
        return content.scrollHeight <= availableHeight && content.scrollWidth <= availableWidth;
      };
      if (fits(maximum)) return;
      let low = 0;
      let high = maximum;
      // No per-card overrides or minimum that would clip dense rules. The rules
      // dialog always provides comfortable text independently of the card fit.
      while (high - low > 0.05) {
        const middle = (low + high) / 2;
        if (fits(middle)) low = middle;
        else high = middle;
      }
      element.style.fontSize = `${low}px`;
    };
    fit();
    const disconnect = observeElementResize(element, fit);
    window.addEventListener('resize', fit);
    document.fonts?.addEventListener('loadingdone', fit);
    void document.fonts?.ready.then(fit);
    return () => {
      disposed = true;
      disconnect();
      window.removeEventListener('resize', fit);
      document.fonts?.removeEventListener('loadingdone', fit);
    };
  }, [contentKey]);
  return <div ref={ref} className={className}><div ref={contentRef} className="digital-item-fit-content">{children}</div></div>;
};

const HandIcon = () => <svg className="digital-item-hand" viewBox="0 0 20 26" width="12" height="19" aria-hidden="true" focusable="false">
  <path fill="currentColor" d="M5 25v-4c-1-1-2-3-3-5L.4 13c-.8-1.6 1-2.8 2-1.4L5 15V5.5c0-1.8 2.2-1.8 2.2 0V11h.6V2c0-1.8 2.3-1.8 2.3 0v9h.6V1.5c0-1.8 2.3-1.8 2.3 0V11h.6V4c0-1.8 2.3-1.8 2.3 0v10c0 3-1 5-2.4 7v4Z" />
</svg>;

const typeIconPaths: Record<string, string> = {
  Tome: 'M12 5C9 3 5 3 2 4v15c3-1 7-1 10 1 3-2 7-2 10-1V4c-3-1-7-1-10 1Zm0 0v15',
  Task: 'M9 5h12M9 12h12M9 19h12M2 5l2 2 3-4M2 12l2 2 3-4M2 19l2 2 3-4',
  Mission: 'M5 22V3c5-4 9 4 15 0v11c-6 4-10-4-15 0',
  Spell: 'm12 2 9 16H3L12 2Zm0 5v15M5 10l14 8M19 10 5 18',
  'Magical spell': 'm12 2 2.5 7.5L22 12l-7.5 2.5L12 22l-2.5-7.5L2 12l7.5-2.5L12 2ZM20 2v4M18 4h4',
  Ally: 'M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM4 22v-3a8 8 0 0 1 16 0v3',
  Skill: 'm12 2 3 6 7 1-5 5 1 8-6-4-6 4 1-8-5-5 7-1 3-6Z',
  Special: 'm12 2 3 2 4 1 1 4 2 3-2 3-1 4-4 1-3 2-3-2-4-1-1-4-2-3 2-3 1-4 4-1 3-2Zm-4 10 3 3 5-6',
  Madness: 'M12 5c-3-5-8-2-7 2-4 1-4 7 0 8-1 5 5 8 7 4 2 4 8 1 7-4 4-1 4-7 0-8 1-4-4-7-7-2Zm0 0v14M5 7l3 2-1 3M19 7l-3 2 1 3M5 15l3-1M19 15l-3-1',
  Injury: 'm3 14 11-11a4 4 0 0 1 7 7L10 21a4 4 0 0 1-7-7Zm5-5 7 7M9 8l7 7M5 16h.01M8 19h.01M16 5h.01M19 8h.01',
  'Common Item': 'M3 8h18v13H3V8Zm5 0V5a4 4 0 0 1 8 0v3M3 12h18M10 11v4h4v-4',
  'Unique Item': 'm3 8 4-5h10l4 5-9 14L3 8Zm0 0h18M7 3l2 5 3 14 3-14 2-5',
  Exhibit: 'M3 9l9-6 9 6H3Zm2 0v10m5-10v10m4-10v10m5-10v10M3 19h18M2 22h20',
};

export const ItemTypeIcon: React.FC<{ label: string }> = ({ label }) => {
  if (label === 'Physical Weapon' || label === 'Magical Weapon') return <WeaponIcon magical={label === 'Magical Weapon'} />;
  return <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" strokeLinecap="round" aria-hidden="true" focusable="false">
    <path d={typeIconPaths[label] ?? 'M4 3h16v18H4V3Zm4 5h8M8 12h8M8 16h5'} />
  </svg>;
};

const formatModifier = (value: number) => value === 99 ? 'X' : value < 0 ? `−${Math.abs(value)}` : `+${value}`;

const WeaponIcon: React.FC<{ magical: boolean }> = ({ magical }) => <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true" focusable="false">
  {magical ? <><path d="m4 20 12-12M12 3l1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3ZM20 11l.8 2.2L23 14l-2.2.8L20 17l-.8-2.2L17 14l2.2-.8L20 11Z" /></>
    : <><path d="m5 19 3-3m-4-3 7 7m-4-5L18 4l3-1-1 4L9 18" /></>}
</svg>;

export const DigitalItemCard: React.FC<{
  card: StackedItemWithKind;
  rules: React.ReactNode;
  typeLabel: string;
  fullArtwork: string;
  annotation?: React.ReactNode;
  annotationKey?: string;
  badge?: React.ReactNode;
  readableContent?: React.ReactNode;
  outcomeStyle?: 'reward' | 'fail';
}> = ({ card, rules, typeLabel, fullArtwork, annotation, annotationKey = '', badge, readableContent, outcomeStyle }) => {
  const item = card.item as CommonItem & UniqueItem & Pick<Spell, 'sanityCost' | 'castingModifier'>;
  const name = item.title?.trim() || item.name;
  const special = card.kind === 'special';
  const usesLabel = card.kind === 'unique' && item.name === 'Ritual Candles' ? 'Clues on card' : 'Uses remaining';
  const miskatonic = useMiskatonicTraumaRules();
  const matchingTrauma = card.kind === 'injury' ? 'Madness' : 'Injury';
  const matchingName = (card.kind === 'injury' ? (card.item as Injury).similarMadnessName
    : (card.item as Madness).similarInjuryName)?.trim();
  const traumaWarning = card.kind === 'injury' || card.kind === 'madness'
    ? `Gaining a duplicate${miskatonic && matchingName ? ` or the ${matchingTrauma} ‘${matchingName}’` : ''} devours you.` : undefined;
  const corruptionType = card.kind === 'corruption' ? corruptionPresentation(card.item as Corruption).cardType : undefined;
  const textOnly = ['skill', 'personalStory', 'relationship', 'madness', 'injury', 'corruption'].includes(card.kind) || (special && !fullArtwork);
  const hasItemStats = special ? item.hands != null : card.kind !== 'ally' && !textOnly;
  const clueCount = special && (item.name === 'The Spores' || ((card.item as SpecialCard).cluesNumber ?? 0) > 0)
    ? (card.item as SpecialCard).cluesNumber ?? 0 : undefined;
  const purchaseContext = useContext(ItemPurchaseContext);
  const purchasePreview = hasItemStats && !special ? purchaseContext : undefined;
  const hasPriceAdjustments = purchasePreview != null && (
    purchasePreview.offeredPrice !== purchasePreview.printedPrice
    || purchasePreview.normalizedPrice !== purchasePreview.offeredPrice
    || purchasePreview.priceAfterFrugal !== purchasePreview.normalizedPrice
    || (purchasePreview.adjustments ?? []).some(adjustment => adjustment.value !== 0)
  );
  const weaponPreview = useItemWeaponPreview(card.kind, (card.item as CommonItem).id);
  const effectiveType = weaponPreview?.effectiveType ?? item.type;
  const effectiveTypeLabel = weaponPreview ? (effectiveType === 'MagicalWeapon' ? 'Magical Weapon' : 'Physical Weapon') : typeLabel;
  const price = hasItemStats && !special ? purchasePreview?.total ?? item.price : undefined;
  const preview = useSpellCastingPreview(card.kind === 'spell' ? (card.item as Spell).id : undefined);
  const sanityCost = preview ? preview.effectiveSanityCost ?? 'X' : item.sanityCost;
  const costBreakdown = preview?.sanityCostBreakdown ?? {
    base: item.sanityCost ?? 0, adjustments: [], adjustedCost: item.sanityCost ?? 0,
    automaticReductions: [], total: item.sanityCost ?? 0, notes: [],
  };
  const hasSanityCostAdjustments = (costBreakdown.adjustments ?? []).some(adjustment => adjustment.value !== 0)
    || (costBreakdown.automaticReductions ?? []).some(reduction => reduction.value !== 0);
  const restrictionId = useId();
  // Game-state polling can recreate React nodes without changing the rules. Refit only
  // when their content/weight changes; ResizeObserver handles metadata/layout changes.
  const rulesKey = JSON.stringify([item.description, card.kind === 'corruption' ? (card.item as Corruption).textDescription : undefined, item.type, item.taskLocations, item.completedTaskLocations,
    item.taskReward, item.missionReward, item.missionSacrifice]);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [artworkOpen, setArtworkOpen] = useState(false);
  const [costOpen, setCostOpen] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [fullFailed, setFullFailed] = useState(false);
  const showType = !!typeLabel.trim() && typeLabel.trim().toLowerCase() !== 'other';
  const resolvedModifier = item.castingModifier === 99 ? preview?.currentCastingModifier : undefined;
  const modifier = resolvedModifier != null
    ? (resolvedModifier < 0 ? `−${Math.abs(resolvedModifier)}` : `+${resolvedModifier}`)
    : item.castingModifier == null ? undefined
    : item.castingModifier === 99 && item.name === 'Call Ancient One' ? '−X'
    : formatModifier(item.castingModifier);
  const stats: [string, string | number | undefined][] = !hasItemStats ? [] : card.kind === 'spell'
    ? [['Hands', item.hands], ['Sanity', sanityCost], ['Modifier', modifier]]
    : [['Cost', price == null ? undefined : `$${price}`], ['Hands', item.hands]];
  const type = showType && <div className="digital-item-type">
      <ItemTypeIcon label={effectiveTypeLabel} />
      {effectiveTypeLabel}
    </div>;
  const metadata = <>{type}
    <dl className="digital-item-stats">{stats.filter(([, value]) => value != null).map(([label, value]) =>
      <div key={label}><dt>{label === 'Sanity' ? 'Sanity cost' : label}</dt><dd>{value}</dd></div>)}</dl>
  </>;
  const hasMetadata = showType || stats.some(([, value]) => value != null);
  const open = (event: React.MouseEvent, target: 'rules' | 'artwork' | 'cost') => {
    event.stopPropagation();
    setFullFailed(false);
    if (target === 'rules') setRulesOpen(true);
    else if (target === 'artwork') setArtworkOpen(true);
    else setCostOpen(true);
  };
  return <article className={`card digital-item-card digital-item-${card.kind}${card.kind === 'personalStory' && outcomeStyle ? ` digital-personal-story-${outcomeStyle}` : ''}${card.kind === 'corruption' ? ` digital-corruption-${corruptionType?.toLowerCase() ?? 'unknown'}` : ''}${preview?.castingBlocked ? ' digital-item-casting-blocked' : ''}`}
    aria-describedby={preview?.castingBlocked ? restrictionId : undefined} onKeyDown={event => event.stopPropagation()}>
    <div className="digital-item-title">
      <FittedCardText className="digital-item-title-text" contentKey={name}>{name}</FittedCardText>
    </div>
    <button type="button" className="digital-item-info" aria-disabled={false} aria-label={`Read rules for ${name}`} aria-haspopup="dialog" onClick={event => open(event, 'rules')}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9" /><path d="M12 11v6" /><circle cx="12" cy="7" r="1" fill="currentColor" stroke="none" /></svg>
    </button>
    {textOnly ? <div className="digital-item-text-header" aria-hidden="true" /> : <div className="digital-item-artwork">
      {previewFailed ? <span className="digital-item-artwork-missing">Artwork unavailable</span>
        : <img src={fullArtwork.replace('/rework/', '/rework/small/')} alt={name} width={240} height={160} loading="lazy" decoding="async" onError={() => setPreviewFailed(true)} />}
      <button type="button" className="digital-item-magnifier" aria-disabled={false} aria-label={`View full artwork for ${name}`} aria-haspopup="dialog" onClick={event => open(event, 'artwork')}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" focusable="false"><circle cx="10" cy="10" r="6" /><path d="m14.5 14.5 6 6" /></svg>
      </button>
    </div>}
    <FittedCardText className="digital-item-rules" contentKey={rulesKey}>{rules}</FittedCardText>
    {(traumaWarning || annotation) && <FittedCardText className="digital-item-annotation" contentKey={traumaWarning ?? annotationKey}>{traumaWarning ?? annotation}</FittedCardText>}
    {badge}
    {clueCount != null && <div className="digital-special-clues" aria-label={`${clueCount} Clue tokens on this card`}>Clues: {clueCount}</div>}
    {(showType || hasItemStats) && <div className={`digital-item-bottom-space${hasItemStats ? ' digital-item-has-stats' : ''}${showType ? ' digital-item-has-type' : ''}`} aria-hidden="true" />}
    {showType && <div className={`digital-item-metadata${hasItemStats ? ' digital-item-metadata-with-stats' : ''}`}>{type}</div>}
    {hasItemStats && <div className="digital-item-footer">
      {item.hands != null && <span className="digital-item-corner digital-item-hands" role="img" aria-label={`${item.hands} hands required`}>
        {item.hands === 0 ? 'X' : <>{Array.from({ length: item.hands }, (_, index) => <HandIcon key={index} />)}</>}
      </span>}
      {card.kind !== 'spell' && (card.kind !== 'unique' || (card.kind === 'unique' && (card.item as UniqueItem).isExhibit !== true)) && price != null && <button type="button" className="digital-item-price-control" aria-disabled={false}
        aria-label={`Cost $${price}`} aria-haspopup="dialog" onClick={event => open(event, 'cost')}>
        <span className="digital-item-corner digital-item-cost" aria-hidden="true">${price}</span>
      </button>}
      {card.kind === 'spell' && <>
        {modifier != null && <span className="digital-item-modifier" role="img" aria-label={`Casting modifier ${modifier}`}>{modifier}</span>}
        {sanityCost != null && <button type="button" className="digital-item-sanity-control" aria-disabled={false}
          aria-label={`Sanity cost ${sanityCost}`} aria-haspopup="dialog" onClick={event => open(event, 'cost')}>
          <span className="digital-item-corner digital-item-sanity" aria-hidden="true"><span className="digital-item-sanity-token"><span className="digital-item-sanity-value">{sanityCost}</span></span></span>
        </button>}
      </>}
    </div>}
    {item.usesRemaining != null && <span className="digital-item-uses" aria-label={usesLabel === 'Clues on card' ? `${usesLabel}: ${item.usesRemaining}` : `${item.usesRemaining} uses remaining`}>{item.usesRemaining}</span>}
    {preview?.castingBlocked && <>
      <svg className="digital-item-casting-cross" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" focusable="false"><path d="M2 2 98 98M98 2 2 98" /></svg>
      <span className="sr-only" id={restrictionId}>Spells cannot be cast: {(preview.blockingSources ?? []).join(', ')}</span>
    </>}
    {(rulesOpen || artworkOpen || costOpen) && <div onClick={event => event.stopPropagation()} onMouseDown={event => event.stopPropagation()}>
      {rulesOpen && <GameDialog title={`${name} rules`} onClose={() => setRulesOpen(false)}>
          <div className="digital-item-readable">{hasMetadata && <div className={`digital-item-metadata digital-item-${card.kind}`}>{metadata}</div>}
            <div className="digital-item-readable-rules">{rules}</div>
            {traumaWarning && <p>{traumaWarning}</p>}
            {readableContent}
            {clueCount != null && <p>Clue tokens on this card: {clueCount}</p>}
            {item.castingModifier === 99 && resolvedModifier != null && preview?.castingModifierExplanation && <p>{preview.castingModifierExplanation}</p>}
            {weaponPreview && <section aria-label="Weapon type"><h3>Weapon type</h3>
              <p>Printed type: {weaponPreview.printedType === 'MagicalWeapon' ? 'Magical Weapon' : 'Physical Weapon'}</p>
              <p>Current type: {effectiveTypeLabel}</p>
              {(weaponPreview.conversionSources ?? []).map((source, index) => <p key={index}>Made magical by: {source}</p>)}
            </section>}
            {card.kind !== 'spell' && price != null && <>
              <ItemPriceBreakdown preview={purchasePreview} printedPrice={item.price ?? price} />
              {hasPriceAdjustments && <button type="button" onClick={event => open(event, 'cost')}>View purchase price breakdown</button>}
            </>}
            {card.kind === 'spell' && sanityCost != null && <>
              {preview?.castingBlocked && <p>Spells cannot be cast: {(preview.blockingSources ?? []).join(', ')}</p>}
              <SpellCostBreakdown breakdown={costBreakdown} />
              {hasSanityCostAdjustments && <button type="button" onClick={event => open(event, 'cost')}>View Sanity cost breakdown</button>}
            </>}
            {item.usesRemaining != null && <p>{usesLabel}: {item.usesRemaining}</p>}
            {!!item.storedMonsters?.length && <p>Stored monsters: {item.storedMonsters.map(monster => `${monster.name} (Toughness ${monster.toughnessBreakdown?.total ?? monster.Toughness ?? monster.toughness})`).join(', ')}</p>}
          </div>
          {card.kind === 'spell' && <SpellRelatedSpecialCard spellName={item.name} />}
      </GameDialog>}
      {artworkOpen && <GameDialog title={`${name} artwork`} variant="artwork" onClose={() => setArtworkOpen(false)}>
        {fullFailed ? <p role="alert">Artwork could not be loaded.</p>
          : <img className="digital-item-artwork-full" src={fullArtwork} alt={`${name} full artwork`} width={1200} height={800} decoding="async" onError={() => setFullFailed(true)} />}
      </GameDialog>}
      {costOpen && <GameDialog title={`${name} ${card.kind === 'spell' ? 'Sanity cost' : 'purchase price'}`} onClose={() => setCostOpen(false)}>
        <div className="digital-item-readable">
          {preview?.castingBlocked && <p>Spells cannot be cast: {(preview.blockingSources ?? []).join(', ')}</p>}
          {card.kind === 'spell' ? <SpellCostBreakdown breakdown={costBreakdown} />
            : <ItemPriceBreakdown preview={purchasePreview} printedPrice={item.price ?? 0} />}
        </div>
      </GameDialog>}
    </div>}
  </article>;
};
